import { P2GBox, P2GButton, P2GText } from '@parcel2go/core-ui';
import { useCallback, useState } from 'react';
import ExistingAccount from '~/apps/account-linking/know-your-customer/existing-account';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { ProviderGroup } from '~/apps/smartsend/lib/helpers/SmartSendBulkActionApplier';
import { AppCard } from '~/components/card/app-card';
import useGoto from '~/hooks/use-goto';
import { Account, Integration } from '~/lib/api/account-linking/types';
import { useAccountLinkingApi } from '~/lib/api/account-linking/use-account-linking-api';
import { SettingsCache } from '~/lib/consts/settings-cache';
import useOnLoad from '~/routes/use-on-load';

type props = {
  children: React.ReactNode;
  providerGroups: ProviderGroup[];
};

type KycStatus = 'LOADING' | 'PENDING' | 'ACTIVE' | 'NOT-STARTED' | 'NOT-REQUIRED';

const KycStatusCheck = ({ children, providerGroups }: props) => {
  const { goTo } = useGoto();
  const { getintegrationsummary } = useAccountLinkingApi();
  const [knowYourCustomerAccount, setKnowYourCustomerAccount] = useState<Account | null>(null);
  const [integration, setIntegration] = useState<Integration | null>(null);

  const [status, setStatus] = useState<KycStatus>('LOADING');

  const checkKycStatus = useCallback(async () => {
    if (!SettingsCache.settings.Features.includes('KnowYourCustomer')) {
      setStatus('NOT-REQUIRED');
      return;
    }

    // Only required if some of the items are international.
    const allItems = providerGroups?.flatMap((x) => x.Items) ?? [];
    if (allItems.length > 0 && allItems.every((i) => i.Recipient.CountryIsoCode === i.Sender.CountryIsoCode)) {
      setStatus('NOT-REQUIRED');
      return;
    }

    const res = await getintegrationsummary('KnowYourCustomer');
    if (res.success) {
      const integrationWithAccount = res.result.find((i) => i.accounts.length > 0);
      if (integrationWithAccount) {
        const account = integrationWithAccount.accounts[0];
        setKnowYourCustomerAccount(account);
        setIntegration(integrationWithAccount);
        setStatus(account.status === 'Active' ? 'ACTIVE' : 'PENDING');
        return;
      }
    }

    setStatus('NOT-STARTED');
  }, [getintegrationsummary, providerGroups]);

  useOnLoad(() => {
    checkKycStatus();
  });

  if (status === 'LOADING') {
    return <P2GBox>Loading...</P2GBox>;
  }

  if (status === 'PENDING') {
    return (
      <AppCard>
        <P2GBox p={4}>
          <ExistingAccount
            showContinueButton={false}
            display="default"
            account={knowYourCustomerAccount}
            integrationSlug={integration.slug}
            integrationType={integration.type}
            onAccountStatusChange={(newStatus) => {
              if (newStatus === 'Active') {
                setStatus('ACTIVE');
              }
            }}
          />
        </P2GBox>
      </AppCard>
    );
  }

  if (status === 'NOT-STARTED') {
    return (
      <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <P2GText>
          To start sending with SmartSend, you'll need to complete the KYC process. Don't worry, it's quick and easy!
        </P2GText>
        <P2GText>Click the button below to start your application.</P2GText>
        <P2GBox>
          <P2GButton onClick={() => goTo(RoutingConstants.VerifyYourIdentity)} variant="primary">
            Start KYC Process
          </P2GButton>
        </P2GBox>
      </P2GBox>
    );
  }

  return <P2GBox>{children}</P2GBox>;
};

export default KycStatusCheck;
